import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { triggerAppliesToBuyer, sequenceAllowsBuyer } from '@dill/dill-shared';
import {
  getSupplierCollectionTriggers,
  addCollectionTriggerTarget,
  removeCollectionTriggerTarget,
} from '../../../../redux/services/collectionTriggerService';
import { getSupplierCollectionSequences } from '../../../../redux/services/collectionSequenceService';
import { DillButton, DillSelect } from '../../../general/Dill';
import { useCollectionBuyerEngagements } from './useCollectionBuyerEngagements';

interface BuyerCollectionTriggerTargetsProps {
  supplierId: string;
  buyerId: string;
  buyerName?: string | null;
}

// Shows every Collection Trigger currently watching this buyer (i.e. this buyer is on its target
// list) - distinct from actually running a sequence, since being targeted just means "waiting for
// the condition to become true" - and lets a staff member add this buyer to a trigger's target
// list directly. Pause/resume doesn't apply to a trigger the way it does to a sequence enrollment;
// the only actions here are adding to / removing from a trigger's target list.
export const BuyerCollectionTriggerTargets = ({
  supplierId,
  buyerId,
  buyerName,
}: BuyerCollectionTriggerTargetsProps) => {
  const dispatch = useAppDispatch();
  const { triggers } = useAppSelector((state) => state.collectionTriggers);
  const { sequences } = useAppSelector((state) => state.collectionSequences);
  const { engagementsByBuyerId, refresh: refreshEngagements } =
    useCollectionBuyerEngagements(supplierId);
  const [pendingTriggerId, setPendingTriggerId] = useState<string | null>(null);
  const [pickedTriggerId, setPickedTriggerId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refresh = () => {
    if (!supplierId) return;
    dispatch(getSupplierCollectionTriggers({ supplierId }));
    dispatch(getSupplierCollectionSequences({ supplierId }));
    refreshEngagements();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const targetingTriggers = useMemo(
    () => triggers.filter((t) => triggerAppliesToBuyer(t, buyerId)),
    [triggers, buyerId]
  );

  const handleRemove = async (triggerId: string) => {
    setPendingTriggerId(triggerId);
    await dispatch(removeCollectionTriggerTarget({ supplierId, triggerId, buyerId }));
    setPendingTriggerId(null);
    refresh();
  };

  const handleAdd = async () => {
    if (!pickedTriggerId) return;
    const triggerId = pickedTriggerId;
    setPickedTriggerId(null);
    setIsAdding(true);
    setErrorMessage(null);
    const result = await dispatch(addCollectionTriggerTarget({ supplierId, triggerId, buyerId }));
    const payload = result.payload as { success?: boolean; errors?: { msg?: string }[] } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(payload?.errors?.[0]?.msg || 'Something went wrong. Please try again.');
    }
    setIsAdding(false);
    refresh();
  };

  // A buyer already targeted by a trigger, or already enrolled in a sequence, can't also be added
  // to another trigger's target list - the mutual-exclusion rule allows at most one engagement. We
  // don't rely solely on the engagement index here - it's a separate doc that can lag behind a
  // sibling component's own action (e.g. enrolling this buyer into a sequence refreshes that
  // component's own engagement hook instance, not this one) - so we also cross-check the raw
  // sequence buyerIds lists directly, same guard pattern used in the trigger/sequence form modals.
  const existingEngagement = engagementsByBuyerId[buyerId];
  const isEnrolledInAnySequence = useMemo(
    () => sequences.some((s) => sequenceAllowsBuyer(s, buyerId)),
    [sequences, buyerId]
  );
  const isBlockedElsewhere =
    isEnrolledInAnySequence || (!!existingEngagement && targetingTriggers.length === 0);

  const addableTriggers = useMemo(
    () => triggers.filter((t) => t.isActive && !triggerAppliesToBuyer(t, buyerId)),
    [triggers, buyerId]
  );

  if (targetingTriggers.length === 0 && addableTriggers.length === 0 && !isBlockedElsewhere) {
    return null;
  }

  const name = buyerName || 'This customer';

  return (
    <div className="flex flex-col gap-2 mb-2">
      <p className="text-xs font-semibold text-gray-700 m-0">Trigger target lists</p>

      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      {targetingTriggers.map((trigger) => (
        <div
          key={trigger.id}
          className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          <p className="text-xs text-amber-800 m-0">
            <span className="font-medium">{name}</span> is on the{' '}
            <span className="font-medium">{trigger.name}</span> target list
            {trigger.sequence?.name ? ` (enrolls into ${trigger.sequence.name})` : ''}.
          </p>
          <button
            type="button"
            disabled={pendingTriggerId === trigger.id}
            onClick={() => handleRemove(trigger.id)}
            className="text-xs font-medium text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
            Remove from trigger
          </button>
        </div>
      ))}

      {isBlockedElsewhere && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-3 py-2 m-0">
          {name} is already{' '}
          {isEnrolledInAnySequence || existingEngagement?.type === 'SEQUENCE_ENROLLED'
            ? 'enrolled in a sequence'
            : 'on another trigger’s target list'}
          . Remove them from it first to add them to a trigger here.
        </p>
      )}

      {!isBlockedElsewhere && targetingTriggers.length === 0 && addableTriggers.length > 0 && (
        <div className="flex items-center gap-2 border border-dashed border-gray-200 rounded-lg px-3 py-2">
          <div className="flex-1 min-w-0">
            <DillSelect
              value={pickedTriggerId ?? ''}
              onChange={setPickedTriggerId}
              options={addableTriggers.map((t) => ({ label: t.name, value: t.id }))}
              placeholder="Add to a trigger's target list…"
            />
          </div>
          <DillButton
            label="Add"
            onClick={handleAdd}
            isDisabled={!pickedTriggerId || isAdding}
            height={34}
          />
        </div>
      )}
    </div>
  );
};

export default BuyerCollectionTriggerTargets;
